import React, { Suspense, useState } from 'react';
import './BotsActivityConnectPage.scss';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { BOTS_ACTIVITY_ANALYSIS, SETTINGS } from '../../shared/routes/path';
import Loader from '../../shared/components/common/loader/Loader';
import Title from '../../shared/components/title/Title';
import { BOT_SOURCES } from '../../shared/constants/botSources';
import { BotSourceType } from '../../shared/types/bot-sources/BotSourceType';
import { useGetCloudflareStatus } from '../../shared/api/hooks/bot-sources/useGetCloudflareStatus';
// Fastly integration is temporarily hidden from clients.
// import { useGetFastlyStatus } from '../../shared/api/hooks/bot-sources/useGetFastlyStatus';
import { useGetLogUploads } from '../../shared/api/hooks/bot-sources/useGetLogUploads';
import { useDisconnectCloudflare } from '../../shared/api/hooks/bot-sources/useDisconnectCloudflare';
// import { useDisconnectFastly } from '../../shared/api/hooks/bot-sources/useDisconnectFastly';
import { useClearLogData } from '../../shared/api/hooks/bot-sources/useClearLogData';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import SourceTile from './components/SourceTile';
import {
  BotSourceKind,
  DataQualityState,
} from './components/data-quality/dataQualityHelpers';

// Fastly is temporarily hidden — keep it in the type but out of the render order.
const SOURCE_ORDER: BotSourceType[] = ['cloudflare', /* 'fastly', */ 'log'];

const parseProjectId = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const BotsActivityConnectPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = parseProjectId(projectIdParam);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isManageMode = searchParams.get('manage') === '1';
  const [activeDialog, setActiveDialog] = useState<BotSourceType | null>(null);
  // Set by the log-upload dialog when an upload parses cleanly but contains 0
  // rows matching the AI-bot catalog. We keep the user on the connect page in
  // that case (instead of bouncing them onto an empty analysis dashboard) and
  // surface a clear notice explaining why nothing landed on the charts.
  const [emptyUploadNotice, setEmptyUploadNotice] = useState<{ rowsParsed: number } | null>(null);

  const cloudflareStatus = useGetCloudflareStatus(projectId);
  // Fastly integration is temporarily hidden from clients.
  // const fastlyStatus = useGetFastlyStatus(projectId);
  const logUploads = useGetLogUploads(projectId);
  const { project, isLoading: projectLoading } = useGetProject(projectId);
  const { disconnectWithConfirm, isPending: disconnectPending } = useDisconnectCloudflare(
    projectId ?? 0,
    cloudflareStatus.data?.integrationId ?? null
  );
  // const { disconnectWithConfirm: disconnectFastlyConfirm, isPending: disconnectFastlyPending } =
  //   useDisconnectFastly(projectId ?? 0, fastlyStatus.data?.integrationId ?? null);
  const { clearWithConfirm, isPending: clearLogPending } = useClearLogData(projectId ?? 0);

  if (projectLoading || cloudflareStatus.isLoading || logUploads.isLoading) {
    return <Loader />;
  }

  // Must mirror the analysis page's `hasIntegration` predicate exactly. After disconnect the
  // backend keeps the integration row (isActive=false) so `integrationId` stays non-null —
  // checking `integrationId !== null` here would loop forever with the analysis page (which
  // uses `isConnected`). Always source connection state from `isConnected`.
  const hasCloudflareIntegration = !!cloudflareStatus.data?.isConnected;
  // Fastly is hidden; force-false so the rest of the predicates short-circuit.
  const hasFastlyIntegration = false;
  const hasLogUploads = logUploads.data.length > 0;
  const hasAnyIntegration = hasCloudflareIntegration || hasFastlyIntegration || hasLogUploads;

  // In manage mode (entered via "Manage integrations" from the analysis page) we render the
  // tiles even when an integration exists, so users can add/remove sources. Without this guard
  // the page would immediately redirect back to /analysis and the button would appear dead.
  if (hasAnyIntegration && projectId && !isManageMode) {
    return <Navigate to={BOTS_ACTIVITY_ANALYSIS(projectId)} replace />;
  }

  const handleConnect = (type: BotSourceType) => {
    setActiveDialog(type);
  };

  const handleDisconnectCloudflare = () => {
    disconnectWithConfirm(() => {
      cloudflareStatus.refetch();
    });
  };

  // Fastly integration is temporarily hidden from clients.
  // const handleDisconnectFastly = () => {
  //   disconnectFastlyConfirm(() => {
  //     fastlyStatus.refetch();
  //   });
  // };

  const handleClearLogData = () => {
    clearWithConfirm(() => {
      logUploads.refetch();
    });
  };

  const handleDialogClose = () => {
    setActiveDialog(null);
  };

  const handleDialogSuccess = (uploadResult?: { rowsParsed: number; rowsMatched: number }) => {
    setActiveDialog(null);
    cloudflareStatus.refetch();
    // fastlyStatus.refetch();
    logUploads.refetch();

    // Log upload with 0 matched rows — keep the user here, show the notice. We
    // still refresh the source-tile state above so the "Active" badge renders
    // correctly; we just skip the navigate to analysis (which would be empty).
    if (uploadResult && uploadResult.rowsParsed > 0 && uploadResult.rowsMatched === 0) {
      setEmptyUploadNotice({ rowsParsed: uploadResult.rowsParsed });
      return;
    }
    setEmptyUploadNotice(null);

    // After a successful CSV upload (or Cloudflare connect), the redirect guard
    // above will eventually fire — but only after both refetches resolve AND
    // the component re-renders. That round-trip can leave the user staring at
    // the connect page for a noticeable beat. Navigate explicitly so the
    // transition feels immediate; the analysis page itself re-validates the
    // integration state and will bounce back here if data is genuinely absent.
    if (projectId) navigate(BOTS_ACTIVITY_ANALYSIS(projectId));
  };

  const sources = SOURCE_ORDER.map((type) => BOT_SOURCES[type]).filter(
    (config): config is NonNullable<typeof config> => config !== undefined
  );

  return (
    <>
      <div className="bots-activity-connect-page page-content">
        <div className="page-box">
          <div className="page-box__filters" data-observe-sticky>
            <Title isBaseCategory={false} title={t('botsActivity.title')}>
              {/* Hide until the project has at least one data source — there is
                  nothing to view on the analysis page otherwise, and showing a
                  primary CTA that leads to an empty state is misleading. The
                  `manage` redirect contract still uses `hasAnyIntegration`, so
                  this guard reuses the same predicate. */}
              {projectId !== null && hasAnyIntegration && (
                <Button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => navigate(BOTS_ACTIVITY_ANALYSIS(projectId))}
                  aria-label={t('botsActivity.connect.viewAnalysisAria') as string}
                >
                  {t('botsActivity.connect.viewAnalysis')}
                </Button>
              )}
            </Title>
          </div>

          <div className="page-box__stats">
            <p className="bots-activity-connect-page__subtitle">
              {t('botsActivity.connect.subtitle')}
            </p>

            {project && !project.website && !project.domain && (
              <div className="warning-box bots-activity-connect-page__alert">
                <div>{t('botAccess.noWebsite')}</div>
                <button
                  className="btn btn--sm btn--secondary"
                  type="button"
                  onClick={() => navigate(SETTINGS)}
                >
                  {t('botAccess.goToSettings')}
                </button>
              </div>
            )}

            {cloudflareStatus.isError && (
              <div className="warning-box warning-box--error bots-activity-connect-page__alert">
                {t('errorLoadingDataRetry')}
              </div>
            )}

            {emptyUploadNotice && (
              <div className="warning-box bots-activity-connect-page__alert">
                <div>
                  {t('botsActivity.connect.noBotsMatched', {
                    count: emptyUploadNotice.rowsParsed,
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn--sm btn--secondary"
                  onClick={() => setEmptyUploadNotice(null)}
                >
                  {t('dismiss')}
                </button>
              </div>
            )}

            <div className="bots-activity-connect-page__list-card">
              <div className="bots-activity-connect-page__list-header">
                <div className="stats-box__title">
                  {t('botsActivity.connect.cdnPartners')}
                </div>
                <div className="bots-activity-connect-page__list-hint">
                  {t('botsActivity.connect.setupHint')}
                </div>
              </div>
                <div className="bots-activity-connect-page__rows">
                  {sources.map((config) => {
                    const isCloudflareActive =
                      config.type === 'cloudflare' && hasCloudflareIntegration;
                    // Fastly integration is temporarily hidden from clients.
                    // const isFastlyActive = config.type === 'fastly' && hasFastlyIntegration;
                    const isLogActive = config.type === 'log' && hasLogUploads;
                    const isActive = isCloudflareActive || isLogActive;

                    // Mutual exclusion: one source per project. When a source is currently
                    // active, every OTHER source is locked until the active one is
                    // disconnected / cleared. The hint under the title tells the user
                    // exactly which action unblocks the tile.
                    const lockedByOtherSource =
                      !isActive && (hasCloudflareIntegration || hasLogUploads);
                    const lockedHintKey = lockedByOtherSource
                      ? hasCloudflareIntegration
                        ? 'botsActivity.sourceLockedByCloudflare'
                        : 'botsActivity.sourceLockedByLogs'
                      : undefined;

                    const onDisconnect = isCloudflareActive
                      ? handleDisconnectCloudflare
                      : isLogActive
                        ? handleClearLogData
                        : undefined;
                    const pending = isCloudflareActive
                      ? disconnectPending
                      : isLogActive
                        ? clearLogPending
                        : false;
                    const disconnectLabelKey = isLogActive ? 'botsActivity.clearLogData' : undefined;
                    const disconnectPendingLabelKey = isLogActive
                      ? 'botsActivity.clearingLogData'
                      : undefined;

                    // The badge surfaces the precision class (AI-verified vs coarser)
                    // of the connected source so the user knows what kind of numbers
                    // they will see on the dashboard before they even open it.
                    const dataQuality: DataQualityState | undefined = isCloudflareActive
                      ? {
                          source: 'cloudflare' as BotSourceKind,
                          supportsBotManagement:
                            cloudflareStatus.data?.supportsBotManagement ?? null,
                        }
                      : undefined;

                    return (
                      <SourceTile
                        key={config.type}
                        config={config}
                        isActive={isActive}
                        onConnect={() => handleConnect(config.type)}
                        onDisconnect={onDisconnect}
                        disconnectPending={pending}
                        disconnectLabelKey={disconnectLabelKey}
                        disconnectPendingLabelKey={disconnectPendingLabelKey}
                        lockedHintKey={lockedHintKey}
                        disabled={lockedByOtherSource}
                        dataQuality={dataQuality}
                      />
                    );
                  })}
              </div>
            </div>

            <p className="bots-activity-connect-page__footer">
              {t('botsActivity.connect.noProvider')}{' '}
              <a
                className="btn--link"
                href="mailto:support@chatbeat.com?subject=Request%20an%20integration"
              >
                {t('botsActivity.connect.requestIntegration')}
              </a>
            </p>
          </div>
        </div>
      </div>

      {activeDialog === 'cloudflare' &&
        projectId &&
        (() => {
          const CloudflareDialog = BOT_SOURCES.cloudflare?.dialogComponent;
          if (!CloudflareDialog) return null;
          return (
            <Suspense fallback={<Loader />}>
              <CloudflareDialog
                projectId={projectId}
                projectDomain={project?.website ?? project?.domain}
                visible={true}
                onClose={handleDialogClose}
                onSuccess={handleDialogSuccess}
              />
            </Suspense>
          );
        })()}

      {/* Fastly integration is temporarily hidden from clients.
      {activeDialog === 'fastly' &&
        projectId &&
        (() => {
          const FastlyDialog = BOT_SOURCES.fastly?.dialogComponent;
          if (!FastlyDialog) return null;
          return (
            <Suspense fallback={<Loader />}>
              <FastlyDialog
                projectId={projectId}
                projectDomain={project?.website ?? project?.domain}
                visible={true}
                onClose={handleDialogClose}
                onSuccess={handleDialogSuccess}
              />
            </Suspense>
          );
        })()}
      */}

      {activeDialog === 'log' &&
        projectId &&
        (() => {
          const LogDialog = BOT_SOURCES.log?.dialogComponent;
          if (!LogDialog) return null;
          return (
            <Suspense fallback={<Loader />}>
              <LogDialog
                projectId={projectId}
                visible={true}
                onClose={handleDialogClose}
                onSuccess={handleDialogSuccess}
                recentUploads={logUploads.data}
              />
            </Suspense>
          );
        })()}

      <ConfirmDialog className="dialog-box" />
    </>
  );
};

export default BotsActivityConnectPage;
